/**
 * Stat growth (the TornStats replacement, docs/work/replace-tornstats-plan.md): gains over a period, gym gains vs. energy
 * from the player's own kept log, a CSV of their battle stats — and, for factions, each sharing member's battle stats
 * TOTAL over time (never single stats: terms v2 shares only the total) with "no growth" flags and a growth rank.
 * Pure: the website and the server use the same copy.
 */
import type { Snapshot } from './web';

const DAY = 86_400_000;

/**
 * From this terms version on, the server keeps a daily history of the battle stats TOTAL a member shares with their
 * faction (400 days, faction mates only, dropped by "Delete my data"). Before it, only the latest share (a day) is kept.
 * The terms text for it is the coordinator's (key-terms.ts); until TERMS_VERSION reaches this, nobody's history is kept.
 */
export const FACTION_GROWTH_TERMS = 8;
/** How long a member's daily totals are kept (like the player's own history). */
export const FACTION_GROWTH_KEEP_DAYS = 400;

export const BATTLE_KEYS = ['strength', 'defense', 'speed', 'dexterity', 'total'] as const;
export type BattleKey = (typeof BATTLE_KEYS)[number];
export const GYM_STATS = ['strength', 'defense', 'speed', 'dexterity'] as const;
export type GymStat = (typeof GYM_STATS)[number];

/** Start of the TCT (UTC) day of `ms`. */
export const tctDay = (ms: number): number => Math.floor(ms / DAY) * DAY;

/** Your battle stats over time as CSV (UTC times; one row per kept snapshot that has them). */
export function battleCsv(snaps: readonly Snapshot[]): string {
  const rows = ['time_utc,strength,defense,speed,dexterity,total'];
  for (const s of snaps) {
    if (!s.stats) continue;
    rows.push([new Date(s.at).toISOString(), ...BATTLE_KEYS.map((k) => String(s.stats![k]))].join(','));
  }
  return `${rows.join('\n')}\n`;
}

/** One stat over time as CSV (a personal stat or a battle stat). */
export function seriesCsv(name: string, points: ReadonlyArray<{ at: number; v: number }>): string {
  const safe = name.replace(/[^\w ]/g, '').trim() || 'value';
  return `time_utc,${safe}\n${points.map((p) => `${new Date(p.at).toISOString()},${p.v}`).join('\n')}\n`;
}

/**
 * "Since your last visit" (TornStats' "record", kept per device by the website): the visit before this one and this one.
 * A visit within 30 minutes of the last keeps the same starting point, so a reload doesn't reset the change to ±0.
 */
export interface BattleVisits<T> {
  prev: { at: number; stats: T } | null;
  cur: { at: number; stats: T } | null;
}
export function nextVisits<T>(v: BattleVisits<T>, stats: T, now: number): BattleVisits<T> {
  const prev = v.cur && now - v.cur.at > 30 * 60_000 ? v.cur : v.prev;
  return { prev, cur: { at: now, stats } };
}

// ---------------------------------------------------------------- gym: gains vs. energy (from the kept log)

export interface GymDay {
  /** Start of the TCT day (ms). */
  day: number;
  trains: number;
  energy: Record<GymStat, number>;
  gain: Record<GymStat, number>;
}

const zero = (): Record<GymStat, number> => ({ strength: 0, defense: 0, speed: 0, dexterity: 0 });
const numOf = (x: unknown): number | null => {
  const n = typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x.trim()) ? Number(x) : x;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};
/** Torn's log types for a gym train: 5300 strength, 5301 defense, 5302 speed, 5303 dexterity. */
const GYM_LOGS: Record<number, GymStat> = { 5300: 'strength', 5301: 'defense', 5302: 'speed', 5303: 'dexterity' };

/** Which stat a gym log entry trained, or null when it isn't a gym train. */
export function gymStatOf(e: { log?: unknown; title?: unknown; data?: unknown }): GymStat | null {
  const byLog = GYM_LOGS[Number(e.log)];
  if (byLog) return byLog;
  const title = typeof e.title === 'string' ? e.title : '';
  if (!/^gym train/i.test(title)) return null;
  const named = GYM_STATS.find((s) => title.toLowerCase().includes(s));
  if (named) return named;
  const data = e.data && typeof e.data === 'object' ? (e.data as Record<string, unknown>) : {};
  return GYM_STATS.find((s) => `${s}_increased` in data || `${s}_after` in data) ?? null;
}

/** One gym train as a log line tells it (the website's energy page reads the same). */
export interface GymTrain {
  stat: GymStat;
  /** Energy spent (≥ 0). */
  energy: number;
  /** Stat gained (≥ 0). */
  gain: number;
  /** How many trains the line is (Torn writes one line per click, which may be several trains). */
  trains: number;
  /** Happiness at the train, when the log says (null: it doesn't). */
  happy: number | null;
}

/** A gym log line (`log` type or title, `data`) → the train, or null when it isn't one. */
export function readGymTrain(e: { log?: unknown; title?: unknown; data?: unknown }): GymTrain | null {
  const stat = gymStatOf(e);
  if (!stat) return null;
  const data = e.data && typeof e.data === 'object' ? (e.data as Record<string, unknown>) : {};
  const before = numOf(data[`${stat}_before`]);
  const after = numOf(data[`${stat}_after`]);
  const gain = numOf(data[`${stat}_increased`]) ?? numOf(data[`${stat}_gained`]) ?? (before !== null && after !== null ? after - before : 0);
  const trains = numOf(data.trains) ?? numOf(data.times) ?? 1;
  const happy = numOf(data.happy_before) ?? numOf(data.happy) ?? null;
  return { stat, energy: Math.max(0, numOf(data.energy_used) ?? 0), gain: Math.max(0, gain), trains: Math.max(1, Math.round(trains)), happy };
}

/** Gym trains from Torn log entries (any order; v1 shape: log, title, timestamp in seconds or ms, data) → per day. */
export function gymFromLog(entries: Iterable<unknown>): GymDay[] {
  const days = new Map<number, GymDay>();
  for (const raw of entries) {
    if (!raw || typeof raw !== 'object') continue;
    const e = raw as { log?: unknown; title?: unknown; timestamp?: unknown; data?: unknown };
    const train = readGymTrain(e);
    const t = numOf(e.timestamp);
    if (!train || t === null || t <= 0) continue;
    const at = t < 1e12 ? t * 1000 : t;
    const day = tctDay(at);
    let d = days.get(day);
    if (!d) days.set(day, (d = { day, trains: 0, energy: zero(), gain: zero() }));
    d.trains++;
    d.energy[train.stat] += train.energy;
    d.gain[train.stat] += train.gain;
  }
  return [...days.values()].sort((a, b) => a.day - b.day);
}

export interface GymTotals {
  trains: number;
  energy: Record<GymStat, number>;
  gain: Record<GymStat, number>;
  /** Stat gained per energy spent on it (null: no energy spent on it). */
  perEnergy: Record<GymStat, number | null>;
  energyAll: number;
  gainAll: number;
}

/** The days from `since` (ms) on, added up. */
export function gymTotals(days: readonly GymDay[], since: number): GymTotals {
  const energy = zero();
  const gain = zero();
  let trains = 0;
  for (const d of days) {
    if (d.day + DAY <= since) continue;
    trains += d.trains;
    for (const s of GYM_STATS) {
      energy[s] += d.energy[s];
      gain[s] += d.gain[s];
    }
  }
  const perEnergy = Object.fromEntries(GYM_STATS.map((s) => [s, energy[s] > 0 ? gain[s] / energy[s] : null])) as Record<GymStat, number | null>;
  const sum = (r: Record<GymStat, number>) => GYM_STATS.reduce((a, s) => a + r[s], 0);
  return { trains, energy, gain, perEnergy, energyAll: sum(energy), gainAll: sum(gain) };
}

// ---------------------------------------------------------------- faction: members' totals over time

export interface GrowthPoint {
  /** Start of the TCT day (ms). */
  day: number;
  total: number;
}

/**
 * How much a member's total grew over the last `days` days: from the newest point at or before that time (or the oldest,
 * if it's within a day of it — a fresh history) to the newest. Null: no point that old.
 */
export function growthOver(points: readonly GrowthPoint[], days: number, now: number): number | null {
  if (!points.length) return null;
  const since = now - days * DAY;
  let base: GrowthPoint | undefined;
  for (const p of points) {
    if (p.day > since) break;
    base = p;
  }
  if (!base) {
    const first = points[0]!;
    if (first.day - since > DAY) return null;
    base = first;
  }
  return points[points.length - 1]!.total - base.total;
}

/** The longest of 30 / 14 / 7 days over which the total didn't grow at all (with history that long), or null. */
export function noGrowthFor(points: readonly GrowthPoint[], now: number): 30 | 14 | 7 | null {
  for (const d of [30, 14, 7] as const) {
    const g = growthOver(points, d, now);
    if (g !== null && g <= 0) return d;
  }
  return null;
}

/** Rank by growth, biggest first (1 = most); members without a figure get no rank. Ties share a rank. */
export function growthRanks(rows: ReadonlyArray<{ id: number; gain: number | null }>): Map<number, number> {
  const sorted = rows.filter((r): r is { id: number; gain: number } => r.gain !== null).sort((a, b) => b.gain - a.gain);
  const out = new Map<number, number>();
  sorted.forEach((r, i) => out.set(r.id, i > 0 && sorted[i - 1]!.gain === r.gain ? out.get(sorted[i - 1]!.id)! : i + 1));
  return out;
}
